<template lang="pug">
div(class="subscription-details-page")
    div(class="header-section")
        button(class="back-btn" @click="goBack")
            b-icon(icon="arrow-left" size="20")
            | Back
        div(class="subscription-loading" v-if="subscriptionLoading")
            div(class="spinner-container")
                b-spinner(variant="primary" label="Loading...")
                p Loading subscription details...
        div(class="subscription-info" v-else-if="subscriptionData")
            h1 {{ subscriptionData.crb5c_title }}
            p(class="desc" :class="{ collapsed: !isDescriptionExpanded, expanded: isDescriptionExpanded }" @click="toggleDescription" v-if="subscriptionData.crb5c_description" v-html="subscriptionData.crb5c_description")
            div(class="meta-info")
                span(class="meta-item" v-if="subscriptionData?.crb5c_amount > 0") 
                    span(class="meta-label") Base Amount:
                    span(class="meta-value") ${{ baseAmount.toFixed(2) }}
                span(class="meta-item" v-if="subscriptionData?.crb5c_amount > 0 && gstPercentage > 0") 
                    span(class="meta-label") GST ({{ gstPercentage }}%):
                    span(class="meta-value") ${{ gstAmount }}
                span(class="meta-item" v-if="subscriptionData?.crb5c_amount > 0") 
                    span(class="meta-label") Total Amount:
                    span(class="meta-value total-highlight") ${{ totalAmount }}
                span(class="meta-item")
                    span(class="meta-label") From:
                    span(class="meta-value") {{ formatDate(subscriptionData.crb5c_datefrom) }}
                span(class="meta-item")
                    span(class="meta-label") To:
                    span(class="meta-value") {{ formatDate(subscriptionData.crb5c_datetill) }}
                //- span(class="meta-item" v-if="subscriptionData?.crb5c_amount > 0  && subscriptionData?.crb5c_recurring !== null")
                //-     span(class="meta-label") Recurring:
                //-     span(class="meta-value") {{ subscriptionData.crb5c_recurring ? 'Yes' : 'No' }}
    
    div(class="content-section")
        div(class="targeted-groups-section" v-if="targetedGroups.length > 0 && !subscriptionLoading")
            div(class="targeted-groups-card")
                h3 Targeted Groups
                div(class="groups-list")
                    span(class="group-badge" v-for="group in targetedGroups" :key="group.id") {{ group.name }}
        
        div(class="card")
            div(class="card-header")
                h2 {{ subscriptionData?.crb5c_amount > 0 ? 'Subscribers' : 'Acknowledgments' }}
                div(class="subscriber-stats" v-if="!subscribersLoading")
                    //- p(v-if="!isTargeted") {{ subscribers.length }} clients have subscribed to this package
                    div(v-if="isTargeted && subscriptionData?.crb5c_amount === 0" class="targeted-stats")
                        //- p {{ actualSubscribers }} out of {{ targetedCount }} targeted clients have acknowledged
                        div(class="stats-breakdown")
                            span(class="stat-item subscribed") {{ actualSubscribers }} Acknowledged
                            span(class="stat-item declined" v-if="declinedCount > 0") {{ declinedCount }} Declined
                            //- span(class="stat-item pending") {{ targetedCount - actualSubscribers - declinedCount }} Pending
                    div(v-else )
                        //- p {{ actualSubscribers }} out of {{ targetedCount }} targeted clients have subscribed
                        div(class="stats-breakdown ")
                            span(class="stat-item subscribed") {{ actualSubscribers }} Subscribed
                            span(class="stat-item declined ml-2" v-if="declinedCount > 0") {{ declinedCount }} Declined
                         
                p(v-else) Loading subscribers...
                
                div(class="action-buttons")
                    b-dropdown(
                        v-if="!reminderLoading"
                        text="Send Reminder"
                        variant="info"
                        right
                        class="reminder-dropdown"
                    )
                        template(#button-content)
                            b-icon(icon="bell" size="16")
                            span Send Reminder
                        b-dropdown-item(@click="sendReminder('push')")
                            b-icon(icon="phone" size="16")
                            span Push Notification
                        b-dropdown-item(@click="sendReminder('email')")
                            b-icon(icon="envelope" size="16")
                            span Email
                    
                    button(class="export-btn sending-reminder" v-else-if="reminderLoading" disabled)
                        b-spinner(small)
                        | Sending...
                    
                    button(class="export-btn" @click="exportSubscribersExcel" v-if="subscribers.length > 0 && !exportLoading" :disabled="exportLoading")
                        b-icon(icon="download" size="16")
                        | Export Excel
                    button(class="export-btn exporting" v-else-if="exportLoading" disabled)
                        b-spinner(small)
                        | Exporting...
            
            div(class="loading-state" v-if="subscribersLoading")
                div(class="spinner-container")
                    b-spinner(variant="primary" label="Loading...")
                    p Loading subscribers...
            
            div(class="error-state" v-else-if="error")
                div(class="error-icon")
                    b-icon(icon="exclamation-triangle" size="48")
                p(class="error-message") {{ error }}
                button(class="retry-btn" @click="fetchSubscribers") 
                    b-icon(icon="arrow-clockwise" size="16")
                    | Retry
            
            div(class="empty-state" v-else-if="!subscribersLoading && subscribers.length === 0")
                div(class="empty-icon")
                    b-icon(icon="people" size="48")
                p(v-if="!isTargeted") No subscribers found for this subscription package.
                p(v-else) No clients have been targeted for this subscription yet.
            
            div(class="subscribers-container" v-else-if="!subscribersLoading")
                div(class="table-container")
                    table(class="subscribers-table")
                        thead
                            tr
                                th Client Name
                                th(v-if="isTargeted") Status
                                th {{ subscriptionData?.crb5c_amount > 0 ? 'Subscribed Date' : 'Acknowledged Date' }}
                                th(v-if="subscriptionData?.crb5c_amount > 0") Invoice Status
                                th(v-if="subscriptionData?.crb5c_amount > 0") Amount Due
                                th(v-if="subscriptionData?.crb5c_amount > 0") Payment Status
                        tbody
                            tr(v-for="subscriber in subscribers" :key="subscriber.id || subscriber.clientId" class="subscriber-row" :class="{ 'pending-row': subscriber.status === 'pending', 'declined-row': subscriber.status === 'declined' }")
                                td(class="client-info")
                                    div(class="client-name") {{ subscriber.clientName }}
                                    //- div(class="client-id") ID: {{ subscriber.clientId }}
                                td(class="status-cell" v-if="isTargeted")
                                    span(class="status-badge" :class="getSubscriptionStatusClass(subscriber.status)")
                                        | {{ getSubscriptionStatusText(subscriber.status) }}
                                td(class="date-cell") 
                                    span(v-if="subscriber.subscribedDate") {{ formatDate(subscriber.subscribedDate) }}
                                    span(v-else class="no-date") -
                                td(class="status-cell" v-if="subscriptionData?.crb5c_amount > 0 && subscriber.status === 'subscribed'")
                                    span(class="status-badge" :class="getInvoiceStatusClass(subscriber.invoice)")
                                        | {{ getInvoiceStatusText(subscriber.invoice) }}
                                td(class="status-cell" v-else-if="subscriptionData?.crb5c_amount > 0")
                                    span(class="status-badge no-invoice") Not Applicable
                                td(class="amount-cell" v-if="subscriptionData?.crb5c_amount > 0 && subscriber.status === 'subscribed'")
                                    span(v-if="subscriber.invoice && subscriber.invoice.outstandingAmount > 0" class="amount-due")
                                        | ${{ subscriber.invoice.outstandingAmount.toFixed(2) }}
                                    span(v-else class="amount-paid") $0.00
                                td(class="amount-cell" v-else-if="subscriptionData?.crb5c_amount > 0")
                                    span(class="no-amount") -
                                td(class="status-cell" v-if="subscriptionData?.crb5c_amount > 0 && subscriber.status === 'subscribed'")
                                    span(class="status-badge" :class="getPaymentStatusClass(subscriber.invoice)")
                                        | {{ getPaymentStatusText(subscriber.invoice) }}
                                td(class="status-cell" v-else-if="subscriptionData?.crb5c_amount > 0")
                                    span(class="status-badge no-payment") Not Applicable
</template>

<script>
import axios from 'axios';
import { mapActions } from 'vuex';

export default {
    name: 'SubscriptionDetailsPage',
    data() {
        return {
            subscriptionData: null,
            subscribers: [],
            subscriptionLoading: false,
            subscribersLoading: false,
            exportLoading: false,
            reminderLoading: false,
            error: '',
            isDescriptionExpanded: false,
            isTargeted: false,
            targetedCount: 0,
            actualSubscribers: 0,
            declinedCount: 0,
            groups: [],
            targetedGroups: []
        }
    },
    computed: {
        subscriptionId() {
            return this.$route.params.subscriptionId;
        },
        baseAmount() {
            return parseFloat(this.subscriptionData?.crb5c_amount || 0);
        },
        gstPercentage() {
            return parseFloat(this.subscriptionData?.crb5c_gst || 0);
        },
        gstAmount() {
            return (this.baseAmount * this.gstPercentage / 100).toFixed(2);
        },
        totalAmount() {
            return (this.baseAmount + parseFloat(this.gstAmount)).toFixed(2);
        }
    },
    async mounted() {
        await this.fetchGroups();
        await this.fetchSubscriptionData();
        await this.fetchSubscribers();
    },
    methods: {
        goBack() {
            this.$router.go(-1);
        },
        formatDate(dateStr, locale = 'en-gb') {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            return d.toLocaleDateString(locale);
        },
        formatDateTime(dateStr, locale = 'en-gb') {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            return d.toLocaleDateString(locale) + ' ' + d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
        },
        toggleDescription() {
            this.isDescriptionExpanded = !this.isDescriptionExpanded;
        },
        async fetchGroups() {
            try {
                const { sessions } = await this.getEveryGroup();
                
                if (sessions && sessions.length > 0) {
                    const sessionMap = sessions.map(item => ({
                        id: item.crb5c_fow_session_scheduleid,
                        name: item.crb5c_session_id,
                        day: item.crb5c_day,
                    }));
                    
                    let sortListGroupSession = this.$root.sortSessionGroupNameByDay(sessionMap);
                    sortListGroupSession.unshift({ id: 'All', name: 'All' });
                    this.groups = sortListGroupSession;
                }
            } catch (err) {
                console.error('Error fetching groups:', err);
            }
        },
        async fetchSubscriptionData() {
            this.subscriptionLoading = true;
            try {
                const res = await axios.get(`/api/subscription/${this.subscriptionId}`);
                this.subscriptionData = res.data.data;
                
          
                if (this.subscriptionData.crb5c_groups) {
                    const groupIds = JSON.parse(this.subscriptionData.crb5c_groups);
                    this.targetedGroups = [];
                    groupIds.forEach(groupId => {
                        const group = this.groups.find(g => g.id === groupId);
                        if (group) {
                            this.targetedGroups.push(group);
                        }
                    });
                }
                
        
                if (this.subscriptionData.crb5c_description && this.subscriptionData.crb5c_description.length <= 150) {
                    this.isDescriptionExpanded = true;
                }
            } catch (err) {
                console.error('Error fetching subscription data:', err);
                this.error = 'Failed to fetch subscription details';
            } finally {
                this.subscriptionLoading = false;
            }
        },
        async fetchSubscribers() {
            this.subscribersLoading = true;
            this.error = '';
            try {
                const res = await axios.get(`/api/subscription/${this.subscriptionId}/subscribers`);
                this.subscribers = res.data.subscribers || [];
                this.isTargeted = res.data.isTargeted || false;
                this.targetedCount = res.data.targetedCount || 0;
                this.actualSubscribers = res.data.actualSubscribers || 0;
                this.declinedCount = res.data.declinedCount || 0;
            } catch (err) {
                console.error('Error fetching subscribers:', err);
                this.error = err?.response?.data?.message || 'Failed to fetch subscribers';
            } finally {
                this.subscribersLoading = false;
            }
        },
        getInvoiceStatusClass(invoice) {
            if (!invoice) return 'no-invoice';
            return invoice.status === 'paid' ? 'invoice-paid' : 'invoice-outstanding';
        },
        getInvoiceStatusText(invoice) {
            if (!invoice) return 'No Invoice';
            return invoice.status === 'paid' ? 'Generated & Paid' : 'Generated';
        },
        getPaymentStatusClass(invoice) {
            if (!invoice) return 'no-payment';
            return invoice.status === 'paid' ? 'payment-paid' : 'payment-outstanding';
        },
        getPaymentStatusText(invoice) {
            if (!invoice) return 'No Payment';
            return invoice.status === 'paid' ? 'Paid' : 'Outstanding';
        },
        getSubscriptionStatusClass(status) {
            switch (status) {
                case 'subscribed': return 'status-subscribed';
                case 'pending': return 'status-pending';
                case 'targeted': return 'status-targeted';
                case 'declined': return 'status-declined';
                default: return 'status-unknown';
            }
        },
        getSubscriptionStatusText(status) {
            switch (status) {
           
                case 'subscribed': return this.subscriptionData?.crb5c_amount === 0 ? 'Acknowledged' : 'Subscribed';
                case 'pending': return 'Pending';
                case 'targeted': return 'Targeted';
                case 'declined': return 'Declined';
                default: return 'Unknown';
            }
        },

        async exportSubscribersExcel() {
            this.exportLoading = true;
            try {
                const response = await axios.get(`/api/subscription/${this.subscriptionId}/subscribers/export`, {
                    responseType: 'blob'
                });
                
         
                const blob = new Blob([response.data], { 
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
                });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                
                const contentDisposition = response.headers['content-disposition'];
                let fileName = `subscription_${this.subscriptionId}_subscribers.xlsx`;
                if (contentDisposition) {
                    const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
                    if (fileNameMatch) {
                        fileName = fileNameMatch[1];
                    }
                }
                
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                
                this.$bvToast.toast('Excel file downloaded successfully', {
                    title: 'Export Complete',
                    variant: 'success',
                    solid: true
                });
            } catch (err) {
                console.error('Error exporting Excel:', err);
                this.$bvToast.toast('Failed to export Excel file', {
                    title: 'Export Error',
                    variant: 'danger',
                    solid: true
                });
            } finally {
                this.exportLoading = false;
            }
        },
        
        async sendReminder(type) {
            const confirmMessage = type === 'push' 
                ? 'Send push notification reminders to all clients who have not responded yet?' 
                : 'Send email reminders to all caregivers of clients who have not responded yet?';
            
            const confirmed = await this.$bvModal.msgBoxConfirm(confirmMessage, {
                title: 'Confirm Send Reminder',
                size: 'md',
                buttonSize: 'sm',
                okVariant: 'primary',
                okTitle: 'Send',
                cancelTitle: 'Cancel',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            });

            if (!confirmed) {
                return;
            }

            this.reminderLoading = true;
            
            try {
                const endpointUrl = type === 'push' 
                    ? `/api/subscription/${this.subscriptionId}/send-reminder-push`
                    : `/api/subscription/${this.subscriptionId}/send-reminder-email`;
                
                const response = await axios.post(endpointUrl);
                
                const pendingCount = response.data.pendingCount || 0;
                
                if (type === 'email') {
                    const emailsSent = response.data.emailsSent || 0;
                    const emailsFailed = response.data.emailsFailed || 0;
                    const clientsWithoutEmails = response.data.clientsWithoutEmails || 0;
                    
                    let message = '';
                    if (pendingCount === 0) {
                        message = 'All targeted clients have already responded.';
                    } else if (emailsSent > 0) {
                        message = `Successfully sent ${emailsSent} email reminder${emailsSent > 1 ? 's' : ''}.`;
                        if (emailsFailed > 0) {
                            message += ` ${emailsFailed} email${emailsFailed > 1 ? 's' : ''} failed to send.`;
                        }
                        if (clientsWithoutEmails > 0) {
                            message += ` ${clientsWithoutEmails} client${clientsWithoutEmails > 1 ? 's have' : ' has'} no caregiver email.`;
                        }
                    } else {
                        message = 'No emails could be sent. Clients may not have caregiver emails registered.';
                    }
                    
                    this.$bvToast.toast(message, {
                        title: 'Email Reminders',
                        variant: emailsSent > 0 ? 'success' : 'warning',
                        solid: true,
                        autoHideDelay: 5000
                    });
                } else {
                    // Push notification
                    const notificationsSent = response.data.notificationsSent || 0;
                    
                    let message = '';
                    if (pendingCount === 0) {
                        message = 'All targeted clients have already responded.';
                    } else {
                        message = `Successfully sent push notification reminders to ${notificationsSent} client${notificationsSent > 1 ? 's' : ''}.`;
                    }
                    
                    this.$bvToast.toast(message, {
                        title: 'Push Notifications',
                        variant: notificationsSent > 0 ? 'success' : 'info',
                        solid: true,
                        autoHideDelay: 5000
                    });
                }
                
            } catch (err) {
                console.error('Error sending reminders:', err);
                const errorMessage = err?.response?.data?.error || err?.response?.data?.message || 'Failed to send reminders';
                
                this.$bvToast.toast(errorMessage, {
                    title: 'Reminder Error',
                    variant: 'danger',
                    solid: true,
                    autoHideDelay: 5000
                });
            } finally {
                this.reminderLoading = false;
            }
        },
        ...mapActions(['getEveryGroup'])
    }
}
</script>

<style lang="scss" scoped>
.subscription-details-page {
    min-height: 100vh;
    background: #f8fafc;
    padding: 24px;

    .header-section {
        margin-bottom: 32px;
        max-width: 75%;
        margin-left: auto;
        margin-right: auto;
        
        .back-btn {
            background: none;
            border: none;
            color: #64748b;
            font-size: 0.95rem;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 24px;
            padding: 8px 12px;
            border-radius: 8px;
            transition: all 0.2s ease;
            
            &:hover {
                background: #e2e8f0;
                color: #475569;
            }
        }

        .spinner-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 48px 24px;
            
            p {
                margin-top: 16px;
                color: #64748b;
                font-size: 0.95rem;
            }
        }
        
        .subscription-info {
            background: white;
            border-radius: 16px;
            padding: 32px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            border: 1px solid #e2e8f0;

            h1 {
                font-size: 2rem;
                font-weight: 600;
                margin-bottom: 8px;
                color: #1e293b;
                line-height: 1.2;
            }
            
            .desc {
                font-size: 1rem;
                color: #64748b;
                margin-bottom: 24px;
                line-height: 1.5;
                cursor: pointer;
                transition: all 0.2s ease;
                
                &.collapsed {
                    display: -webkit-box;
                    -webkit-line-clamp: 3;
                    line-clamp: 3;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    
                    &:after {
                        content: '... Show more';
                        color: #3b82f6;
                        font-weight: 500;
                        margin-left: 4px;
                    }
                }
                
                &.expanded:after {
                    content: ' Show less';
                    color: #3b82f6;
                    font-weight: 500;
                    margin-left: 4px;
                }
            }
            
            .meta-info {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                gap: 16px;
                
                .meta-item {
                    background: #f8fafc;
                    padding: 16px;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    
                    .meta-label {
                        display: block;
                        font-size: 0.875rem;
                        color: #64748b;
                        margin-bottom: 4px;
                        font-weight: 500;
                    }
                    
                    .meta-value {
                        display: block;
                        font-size: 1rem;
                        color: #1e293b;
                        font-weight: 600;
                        
                        &.total-highlight {
                            color: #059669;
                            font-size: 1.125rem;
                        }
                    }
                }
            }
        }
    }

    .content-section {
        max-width: 75%;
        margin: 0 auto;
        
        .targeted-groups-section {
            margin-bottom: 24px;
            
            .targeted-groups-card {
                background: white;
                border-radius: 16px;
                padding: 24px 32px;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                border: 1px solid #e2e8f0;
                
                h3 {
                    font-size: 1.25rem;
                    font-weight: 600;
                    margin: 0 0 16px 0;
                    color: #1e293b;
                }
                
                .groups-list {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 8px;
                    
                    .group-badge {
                        background: #e0e7ff;
                        color: #3730a3;
                        padding: 8px 16px;
                        border-radius: 20px;
                        font-size: 0.875rem;
                        font-weight: 500;
                        border: 1px solid #c7d2fe;
                    }
                }
            }
        }

        .card {
            background: white;
            border-radius: 16px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            border: 1px solid #e2e8f0;
            overflow: hidden;
            
            .card-header {
                padding: 24px 32px;
                border-bottom: 1px solid #e2e8f0;
                background: #fafbfc;
                display: flex;
                justify-content: space-between;
                align-items: center;
                flex-wrap: wrap;
                gap: 16px;
                
                h2 {
                    font-size: 1.25rem;
                    font-weight: 600;
                    margin: 0;
                    color: #1e293b;
                }
                
                p {
                    color: #64748b;
                    margin: 0;
                    flex: 1;
                    font-size: 0.95rem;
                }
                
                .subscriber-stats {
                    flex: 1;
                    
                    p {
                        margin: 0;
                        color: #64748b;
                        font-size: 0.95rem;
                    }
                    
                    .targeted-stats {
                        .stats-breakdown {
                            display: flex;
                            gap: 16px;
                            margin-top: 8px;
                            
                            .stat-item {
                                padding: 4px 12px;
                                border-radius: 12px;
                                font-size: 0.8rem;
                                font-weight: 600;
                                
                                &.subscribed {
                                    background: #dcfce7;
                                    color: #166534;
                                }
                                
                                &.declined {
                                    background: #fee2e2;
                                    color: #991b1b;
                                }
                                
                                &.pending {
                                    background: #fef3c7;
                                    color: #92400e;
                                }
                            }
                        }
                    }
                }
                
                .action-buttons {
                    display: flex;
                    gap: 12px;
                    align-items: center;
                    
                    .reminder-dropdown {
                        ::v-deep .btn {
                            background: #3b82f6;
                            border: none;
                            border-radius: 8px;
                            padding: 10px 16px;
                            font-size: 0.875rem;
                            font-weight: 500;
                            display: flex;
                            align-items: center;
                            gap: 8px;
                            transition: all 0.2s ease;
                            
                            &:hover {
                                background: #2563eb;
                                transform: translateY(-1px);
                            }
                        }
                        
                        ::v-deep .dropdown-item {
                            display: flex;
                            align-items: center;
                            gap: 8px;
                            padding: 10px 16px;
                            
                            &:hover {
                                background: #f1f5f9;
                            }
                        }
                    }
                }
                
                .export-btn {
                    background: #10b981;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    padding: 10px 16px;
                    font-size: 0.875rem;
                    font-weight: 500;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.2s ease;
                    
                    &:hover:not(:disabled) {
                        background: #059669;
                        transform: translateY(-1px);
                    }
                    
                    &.exporting, &.sending-reminder {
                        background: #64748b;
                        cursor: not-allowed;
                    }
                    
                    &:disabled {
                        opacity: 0.7;
                        cursor: not-allowed;
                        transform: none;
                    }
                }
            }
        }
    }

    .loading-state, .error-state, .empty-state {
        padding: 64px 32px;
        text-align: center;

        .spinner-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            
            p {
                margin-top: 16px;
                color: #64748b;
                font-size: 0.95rem;
            }
        }
    }

    .error-state {
        .error-icon {
            margin-bottom: 16px;
            color: #ef4444;
        }

        .error-message {
            color: #ef4444;
            margin-bottom: 24px;
            font-size: 1rem;
        }
        
        .retry-btn {
            background: #3b82f6;
            color: white;
            border: none;
            border-radius: 8px;
            padding: 12px 24px;
            cursor: pointer;
            font-weight: 500;
            display: flex;
            align-items: center;
            gap: 8px;
            margin: 0 auto;
            transition: all 0.2s ease;

            &:hover {
                background: #2563eb;
                transform: translateY(-1px);
            }
        }
    }

    .empty-state {
        .empty-icon {
            margin-bottom: 16px;
            color: #94a3b8;
        }

        p {
            color: #64748b;
            font-size: 1rem;
        }
    }

    .subscribers-container {
        .table-container {
            max-height: 600px;
            overflow-y: auto;
            border-radius: 0 0 16px 16px;
            
            &::-webkit-scrollbar {
                width: 8px;
            }
            
            &::-webkit-scrollbar-track {
                background: #f1f5f9;
            }
            
            &::-webkit-scrollbar-thumb {
                background: #cbd5e1;
                border-radius: 4px;
                
                &:hover {
                    background: #94a3b8;
                }
            }
        }

        .subscribers-table {
            width: 100%;
            border-collapse: collapse;
            
            th {
                background: #f8fafc;
                padding: 16px 24px;
                text-align: left;
                font-weight: 600;
                color: #475569;
                font-size: 0.875rem;
                border-bottom: 1px solid #e2e8f0;
                position: sticky;
                top: 0;
                z-index: 10;
            }
            
            .subscriber-row {
                transition: all 0.2s ease;
                
                &:hover {
                    background: #f8fafc;
                }
                
                td {
                    padding: 20px 24px;
                    border-bottom: 1px solid #f1f5f9;
                    vertical-align: middle;
                    text-align: left;
                }
            }
            
            .client-info {
                .client-name {
                    font-weight: 600;
                    color: #1e293b;
                    font-size: 0.95rem;
                    margin-bottom: 4px;
                }
                
                .client-id {
                    font-size: 0.875rem;
                    color: #64748b;
                }
            }
            
            .date-cell {
                color: #475569;
                font-size: 0.9rem;
            }
            
            .status-cell {
                .status-badge {
                    padding: 6px 12px;
                    border-radius: 20px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    display: inline-block;
                }
            }
            
            .amount-cell {
                font-weight: 600;
                font-size: 0.95rem;
            }
            
            // Status colors
            .invoice-paid, .payment-paid {
                background: #dcfce7;
                color: #166534;
            }
            
            .invoice-outstanding, .payment-outstanding {
                background: #fef3c7;
                color: #92400e;
            }
            
            .no-invoice, .no-payment {
                background: #f1f5f9;
                color: #64748b;
            }
            
            .status-subscribed {
                background: #dcfce7;
                color: #166534;
            }
            
            .status-pending {
                background: #fef3c7;
                color: #92400e;
            }
            
            .status-declined {
                background: #fee2e2;
                color: #991b1b;
            }
            
            .status-targeted {
                background: #e0e7ff;
                color: #3730a3;
            }
            
            .status-unknown {
                background: #f1f5f9;
                color: #64748b;
            }
            
            .amount-due {
                color: #dc2626;
            }
            
            .amount-paid {
                color: #059669;
            }
            
            .no-date, .no-amount {
                color: #94a3b8;
                font-style: italic;
            }
            
            .pending-row {
                background: #fefbf3;
                
                &:hover {
                    background: #fef3c7;
                }
            }
            
            .declined-row {
                background: #fef2f2;
                
                &:hover {
                    background: #fee2e2;
                }
            }
        }
    }
}

@media (max-width: 768px) {
    .subscription-details-page {
        padding: 16px;
        
        .header-section {
            max-width: 95%;
            
            .subscription-info {
                padding: 24px 20px;

                h1 {
                    font-size: 1.75rem;
                }
                
                .meta-info {
                    grid-template-columns: 1fr;
                    gap: 12px;
                }
            }
        }
        
        .content-section {
            max-width: 95%;
            
            .targeted-groups-section {
                .targeted-groups-card {
                    padding: 20px;
                    
                    h3 {
                        font-size: 1.125rem;
                    }
                    
                    .groups-list {
                        .group-badge {
                            font-size: 0.8rem;
                            padding: 6px 12px;
                        }
                    }
                }
            }
        }
        
        .content-section .card {
            .card-header {
                padding: 20px;
                flex-direction: column;
                align-items: flex-start;
                gap: 12px;
                
                .subscriber-stats {
                    .targeted-stats .stats-breakdown {
                        flex-direction: column;
                        gap: 8px;
                        
                        .stat-item {
                            text-align: left;
                            width: fit-content;
                        }
                    }
                }
                
                .action-buttons {
                    width: 100%;
                    flex-direction: column;
                    
                    .reminder-dropdown {
                        width: 100%;
                        
                        ::v-deep .btn {
                            width: 100%;
                            justify-content: center;
                        }
                    }
                    
                    .export-btn {
                        width: 100%;
                        justify-content: center;
                    }
                }
            }
        }
        
        .subscribers-container .table-container {
            max-height: 500px;
            
            .subscribers-table {
                th, .subscriber-row td {
                    padding: 16px 12px;
                }
                
                th {
                    font-size: 0.8rem;
                }
                
                .client-info .client-name {
                    font-size: 0.9rem;
                }
                
                .status-badge {
                    font-size: 0.7rem;
                    padding: 4px 8px;
                }
            }
        }
    }
}
</style>